import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { RadioButton } from 'primereact/radiobutton';
import { Tag } from 'primereact/tag';
import { flagDefinitionsApi } from '../../api/flagSystemApi';
import {
  emptyFlag, FLAG_DAYS, FLAG_ROLE_LABELS, flagAccessSummary, flagError,
  flagFieldErrors, flagRequest, validateFlag,
} from '../../utils/flagSystem';
import { Feedback, Field, FlagPage, ResourceState, useRemoteResource } from './FlagSystemCommon';
import { confirmFlagApplicability, FLAG_APPLICABILITY_OPTIONS, flagApplicabilityFields,
  flagApplicabilityLabel, flagApplicabilityValue, hasFlagApplicability } from '../../utils/flagApplicability';

const actionFields = [
  ['juniorAction', 'Junior Local User', 'JuniorLocalUser', 'e.g. Deny entry, contact manager'],
  ['dutyManagerAction', 'Duty Manager', 'DutyManager', 'e.g. Deny entry, escalate if needed'],
  ['seniorAction', 'Senior Local User', 'SeniorLocalUser', 'e.g. Use discretion, may override'],
];
const orderedPermissions = (flag) => [...flag.permissions].sort((a, b) =>
  actionFields.findIndex(field => field[2] === a.role) - actionFields.findIndex(field => field[2] === b.role));
const editableFlag = (flag) => ({
  ...emptyFlag(), ...flag,
  applyOnEntry: flag.applyOnEntry, applyOnExit: flag.applyOnExit,
  description: flag.description || '', juniorAction: flag.juniorAction || '',
  dutyManagerAction: flag.dutyManagerAction || '', seniorAction: flag.seniorAction || '',
  times: flag.times.map(time => ({ ...time })),
  permissions: flag.permissions.map(permission => ({ ...permission })),
});

const FlagManagement = ({ account, onAccessError }) => {
  const navigate = useNavigate();
  const { customerId } = account;
  const loader = useCallback(() => flagDefinitionsApi.list(customerId), [customerId]);
  const resource = useRemoteResource(loader, onAccessError);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(emptyFlag);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [hideDisabled, setHideDisabled] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const busy = useRef(false);
  const applicabilitySupported = Boolean(editor && editor.applicabilitySupported);
  const flags = resource.data || [];
  const visibleFlags = hideDisabled ? flags.filter(flag => flag.active) : flags;

  const open = (flag, duplicate = false) => {
    setEditor({ flagId: flag && !duplicate ? flag.flagId : null, sourceName: flag && flag.name, duplicate,
      applicabilitySupported: flag ? hasFlagApplicability(flag) :
        Boolean(resource.data && resource.data.length && resource.data.every(hasFlagApplicability)) });
    setForm(flag ? { ...editableFlag(flag), ...(duplicate ? { name: `${flag.name} (copy)`.slice(0, 200), revision: 0, active: true } : {}) } : emptyFlag());
    setErrors({}); setError(''); setMessage(''); setConflict(false); setUnverified(false);
  };
  const close = () => { if (!saving) { setEditor(null); setForm(emptyFlag()); setErrors({}); setError(''); setConflict(false); } };
  const clear = () => {
    setForm(previous => ({ ...emptyFlag(), revision: previous.revision, active: previous.active,
      ...(editor.flagId ? { applyOnEntry: previous.applyOnEntry, applyOnExit: previous.applyOnExit } : {}) }));
    setErrors({});
  };
  const change = (field, value) => setForm(previous => ({ ...previous, [field]: value }));
  const handleFailure = (failure, fallback) => {
    onAccessError(failure);
    setError(flagError(failure, fallback));
    setErrors(flagFieldErrors(failure));
    if (failure.response && failure.response.status === 409) setConflict(true);
  };
  const save = async () => {
    if (busy.current || unverified) return;
    const validation = validateFlag(form, { validateApplicability: applicabilitySupported });
    setErrors(validation);
    if (Object.keys(validation).length || conflict) return;
    busy.current = true; setSaving(true); setError('');
    let written = false;
    let savedId = editor.flagId;
    try {
      const request = flagRequest(form, applicabilitySupported);
      const saved = editor.flagId ? await flagDefinitionsApi.update(customerId, editor.flagId, request) :
        await flagDefinitionsApi.create(customerId, request);
      written = true;
      savedId = (saved && saved.flagId) || savedId;
      if (applicabilitySupported) {
        confirmFlagApplicability(saved, request, savedId);
        if (!savedId) throw new Error('The server did not return a flag ID for persistence verification.');
        confirmFlagApplicability(await flagDefinitionsApi.get(customerId, savedId), request, savedId);
      }
      setEditor(null); setForm(emptyFlag());
      setMessage(applicabilitySupported ? 'Flag saved. Applicability confirmed after reload.' : 'Flag saved without applicability editing. Selected applicability was not sent or verified.');
      resource.reload();
    } catch (failure) {
      if (written || failure.code === 'FLAG_APPLICABILITY_UNVERIFIED' ||
          (applicabilitySupported && (!failure.response || failure.response.status >= 500))) {
        setUnverified(true);
        setEditor(previous => ({ ...previous, flagId: savedId || failure.savedFlagId || null }));
        setError(`Applicability integration is unverified. Your selection is preserved; do not repeat the save. ${failure.message || 'Reload failed after the write.'}`);
        onAccessError(failure);
        resource.reload();
      } else handleFailure(failure, 'Unable to save the flag.');
    } finally { busy.current = false; setSaving(false); }
  };
  const disable = async (flag) => {
    setSaving(true); setError(''); setMessage('');
    try {
      await flagDefinitionsApi.disable(customerId, flag.flagId, flag.revision);
      setConfirmation(null); setMessage('Flag disabled. Existing assignments are retained.'); resource.reload();
    } catch (failure) {
      handleFailure(failure, 'Unable to disable the flag.');
      setConfirmation(null);
    } finally { setSaving(false); }
  };
  const reloadEditor = async () => {
    setConfirmation(null); setSaving(true); setError('');
    try {
      const latest = await flagDefinitionsApi.get(customerId, editor.flagId);
      if (unverified) {
        confirmFlagApplicability(latest, form, editor.flagId);
        setForm(editableFlag(latest)); setUnverified(false); setErrors({}); setConflict(false);
        setMessage('Saved applicability confirmed after reload.');
        resource.reload();
        return;
      }
      setForm(editableFlag(latest)); setErrors({}); setConflict(false);
      setEditor(previous => ({ ...previous, applicabilitySupported: hasFlagApplicability(latest) }));
    } catch (failure) { handleFailure(failure, 'Unable to reload the flag.'); }
    finally { setSaving(false); }
  };
  const permissionList = (flag, property) => {
    const permitted = orderedPermissions(flag).filter(permission => permission[property]);
    return permitted.length ? <div className="flag-definition-permissions">
      {permitted.map(permission => <Tag key={permission.role} value={FLAG_ROLE_LABELS[permission.role]} severity="success" />)}
    </div> : <span className="flag-definition-none">{property === 'canOverride' ? 'No overrides permitted' : 'No roles permitted'}</span>;
  };

  return <div className="flag-management-surface"><FlagPage title="Flag Management" className="flag-management-page" containerFluid="md" actions={<>
    <Button label="Audit Log" icon="pi pi-history" className="p-button-text" disabled={saving} onClick={() => navigate('/support/flags/audit-log')} />
    <Button aria-label="Refresh flags" title="Refresh flags" icon="pi pi-refresh" className="p-button-text p-button-rounded" disabled={saving} onClick={() => { setError(''); setConflict(false); resource.reload(); }} />
    <Button label="Create Flag" icon="pi pi-plus" className="p-button-success" onClick={() => open(null)} disabled={saving || resource.loading || Boolean(resource.error)} />
  </>}>
    <Feedback error={!editor ? error : ''} message={message} />
    {resource.data && (!resource.data.length || resource.data.some(flag => !hasFlagApplicability(flag))) &&
      <Message severity="warn" text="BACKEND NOT YET AVAILABLE: flag definition responses do not verify applyOnEntry/applyOnExit. Applicability editing is unavailable for those records. Existing configuration editing remains available without sending unsupported fields." />}
    {!editor && conflict && <Message severity="warn" text="The flag changed. Refresh the list before attempting another change." />}
    <ResourceState resource={resource}>
      <div className="flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h5 className="m-0">{hideDisabled ? 'Enabled Flags' : 'All Flags'}</h5>
        <div className="flex align-items-center flex-wrap gap-3">
          <div className="flex align-items-center gap-2">
            <input id="hide-disabled-flags" className="flag-status-filter" type="checkbox" checked={hideDisabled}
              onChange={event => setHideDisabled(event.target.checked)} />
            <label htmlFor="hide-disabled-flags">Hide disabled flags</label>
          </div>
          <Tag value={hideDisabled ? `${visibleFlags.length} of ${flags.length} flags` :
            `${flags.length} ${flags.length === 1 ? 'flag' : 'flags'}`} />
        </div>
      </div>
      <DataTable className="flag-definitions-table" value={visibleFlags} dataKey="flagId"
        emptyMessage={<div className="flag-definition-empty">
          <i className="pi pi-flag" aria-hidden="true" />
          <strong>{hideDisabled && flags.length ? 'No enabled flags' : 'No flags created yet'}</strong>
          {hideDisabled && flags.length ? 'Uncheck "Hide disabled flags" to view disabled flags.' :
            <>Use the &quot;Create Flag&quot; button above to add your first access-control flag.</>}
        </div>} responsiveLayout="stack" breakpoint="860px">
        <Column field="name" header="Flag Name" body={flag => <div>
          <span className="flag-definition-name">{flag.name}</span>
          {!flag.active && <div className="mt-2"><Tag value="Disabled" severity="warning" /></div>}
        </div>} />
        <Column field="description" header="Description" body={flag => flag.description ?
          <span className="flag-definition-description">{flag.description}</span> : <span className="flag-definition-none">No description</span>} />
        <Column header="Applies to" body={flag => <Tag value={flagApplicabilityLabel(flag)}
          severity={!flagApplicabilityValue(flag) ? 'warning' : 'info'} />} />
        <Column header="Access Rule" body={flag => <>
          <Tag value={flag.blockAllTime ? 'Block all time' : 'Scheduled'} severity={flag.blockAllTime ? 'danger' : 'info'} />
          {!flag.blockAllTime && <div className="flag-definition-schedule-summary">{flagAccessSummary(flag)}</div>}
        </>} />
        <Column header="Override Permissions" body={flag => permissionList(flag, 'canOverride')} />
        <Column header="Add/Remove Permissions" body={flag => permissionList(flag, 'canAddRemove')} />
        <Column header="Actions" body={flag => <div className="flag-definition-actions">
          <Button aria-label={`Edit ${flag.name}`} title="Edit flag" icon="pi pi-pencil" className="p-button-text p-button-rounded" disabled={saving} onClick={() => open(flag)} />
          <Button aria-label={`Duplicate ${flag.name}`} title="Duplicate flag" icon="pi pi-copy" className="p-button-text p-button-rounded" disabled={saving} onClick={() => open(flag, true)} />
          <Button aria-label={`${flag.active ? 'Disable' : 'Re-enable'} ${flag.name}`} title={flag.active ? 'Disable flag' : 'Re-enable flag'} icon={flag.active ? 'pi pi-ban' : 'pi pi-check'}
            className={`p-button-text p-button-rounded ${flag.active ? 'p-button-danger' : 'p-button-success'}`} disabled={saving || conflict}
            onClick={() => flag.active ? setConfirmation({ kind: 'disable', flag }) : setConfirmation({ kind: 'enable', flag })} />
        </div>} />
      </DataTable>
    </ResourceState>

    <Dialog header={editor && editor.flagId ? 'Edit Flag' : editor && editor.duplicate ? 'Duplicate Flag' : 'Create a Flag'} visible={Boolean(editor)}
      className="flag-editor flag-definition-editor" position="top" draggable={false} resizable={false} modal closable={!saving} onHide={close}
      footer={<div className="flex justify-content-center flex-wrap gap-3">
        <Button label="Clear Form" icon="pi pi-times" className="p-button-warning" onClick={clear} disabled={saving || unverified} />
        <Button label="Save Flag" icon="pi pi-check" className="p-button-success" onClick={save} loading={saving} disabled={saving || conflict || unverified} />
      </div>}>
      {editor && editor.sourceName && <div className={`flag-definition-banner ${editor.duplicate ? 'flag-definition-banner-copy' : ''}`}>
        <span><i className={editor.duplicate ? 'pi pi-copy mr-2' : 'pi pi-pencil mr-2'} aria-hidden="true" />
          {editor.duplicate ? 'Duplicating flag: ' : 'Editing flag: '}{editor.sourceName}</span>
        <Button label={editor.duplicate ? 'Cancel duplicate' : 'Cancel editing'} className="p-button-link p-button-sm" onClick={close} disabled={saving} />
      </div>}
      <Feedback error={error} />
      {unverified && <div className="mb-3"><Message severity="warn" text="The write may already have succeeded, but applicability persistence is unverified. Your selection has not been discarded. No automatic retry will be made." />
        {editor.flagId && <Button label="Verify saved applicability" className="p-button-text" disabled={saving} onClick={reloadEditor} />}</div>}
      {conflict && <div className="mb-3"><Message severity="warn" text="The configuration changed. Your edits are preserved. Copy or reconcile them before loading the latest revision." />
        <Button label="Reload latest" className="p-button-text" disabled={saving} onClick={() => setConfirmation({ kind: 'reload' })} /></div>}
      <fieldset disabled={unverified} className="border-0 p-0">
      <h5 className="flag-definition-section flag-definition-section-first">Flag Details</h5>
      <Field id="flag-name" label={<>Flag Name <span className="flag-definition-optional">(required)</span></>} error={errors.name}>
        <InputText id="flag-name" value={form.name} placeholder="e.g. Suspended Member" maxLength={200} disabled={saving}
          className={errors.name ? 'p-invalid' : ''} aria-required="true" aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? 'flag-name-error' : undefined} onChange={event => change('name', event.target.value)} />
      </Field>
      <Field id="flag-description" label={<>Flag Description <span className="flag-definition-optional">(optional)</span></>} error={errors.description}>
        <InputTextarea id="flag-description" value={form.description} placeholder="Describe when and why this flag is used..."
          maxLength={2000} rows={3} disabled={saving} onChange={event => change('description', event.target.value)} />
      </Field>
      <section aria-labelledby="flag-applicability-heading">
        <h5 id="flag-applicability-heading" className="flag-definition-section">Applies to</h5>
        <p className="flag-definition-hint">Choose which access-point directions this flag affects.</p>
        {!applicabilitySupported && <Message severity="warn" text="BACKEND NOT YET AVAILABLE: applyOnEntry/applyOnExit must be exposed by the definition request/response DTOs. No selected applicability will be sent or claimed as saved." />}
        {hasFlagApplicability(form) && !flagApplicabilityValue(form) && <Message severity="warn" text="Configuration warning: neither direction is selected. Choose a valid applicability before saving." />}
        <div role="radiogroup" aria-labelledby="flag-applicability-heading">
          {FLAG_APPLICABILITY_OPTIONS.map(option => <div key={option.value} className={`flag-definition-radio-option ${flagApplicabilityValue(form) === option.value ? 'is-selected' : ''}`}>
            <input type="radio" id={`flag-scope-${option.value}`} className="flag-applicability-radio" name="flagApplicability"
              value={option.value} checked={flagApplicabilityValue(form) === option.value}
              disabled={saving || unverified || !applicabilitySupported}
              onChange={() => setForm(previous => ({ ...previous, ...flagApplicabilityFields(option.value) }))} />
            <label htmlFor={`flag-scope-${option.value}`}>{option.value === 'both' ? 'Entry and exit' : option.label}</label>
          </div>)}
        </div>
        {errors.applicability && <Message severity="error" text={errors.applicability} />}
      </section>
      <h5 className="flag-definition-section">Action Wording</h5>
      <p className="flag-definition-hint">What should be shown / said to staff at the access point for this flag, per user level. Wording is optional.</p>
      <div className="flag-definition-role-grid mb-3">
        {actionFields.map(([field, label, , placeholder]) => <Field key={field} id={field} label={label} error={errors[field]}>
          <InputText id={field} value={form[field]} placeholder={placeholder} maxLength={2000} disabled={saving} onChange={event => change(field, event.target.value)} />
        </Field>)}
      </div>
      <h5 className="flag-definition-section">Access Rule</h5>
      <p className="flag-definition-hint">These shared access rules apply only within the selected direction(s). Block all the time blocks those directions, not the other direction.</p>
      {[{ block: true, id: 'block-all', title: 'Block access all the time', description: 'This flag blocks its selected direction(s), regardless of day or time.' },
        { block: false, id: 'allow-schedule', title: 'Allow configured days & times', description: 'Select the days and time windows during which this flag allows access.' }].map(option =>
        <div key={option.id} className={`flag-definition-radio-option ${form.blockAllTime === option.block ? 'is-selected' : ''}`}>
          <RadioButton inputId={option.id} name="accessMode" checked={form.blockAllTime === option.block} disabled={saving}
            onChange={() => { if (form.blockAllTime !== option.block) setForm(previous => ({ ...previous, blockAllTime: option.block, times: [] })); }} />
          <label htmlFor={option.id}><span className="flag-definition-option-title">{option.title}</span>
            <span className="flag-definition-option-description">{option.description}</span></label>
        </div>)}
      <div className={`flag-definition-schedule ${form.blockAllTime ? 'is-disabled' : ''}`}>
        <p className="flag-definition-hint">British wall-clock time (GMT/BST). Use HH:mm; end time may be 24:00.</p>
        {FLAG_DAYS.map(day => {
          const index = form.times.findIndex(time => time.dayOfWeek === day.value);
          const time = form.times[index];
          return <div key={day.value} className="flag-definition-day">
            <div className="flag-definition-day-row">
              <div className="flag-definition-day-check">
                <Checkbox inputId={`day-${day.value}`} checked={Boolean(time)} disabled={saving || form.blockAllTime}
                  onChange={event => change('times', event.checked ? [...form.times, { dayOfWeek: day.value, start: '09:00', end: '17:00' }] : form.times.filter(item => item.dayOfWeek !== day.value))} />
                <label htmlFor={`day-${day.value}`}>{day.label}</label>
              </div>
              {['start', 'end'].map(property => <Field key={property} id={`${property}-${day.value}`} label={property === 'start' ? 'Start' : 'End'}>
                <InputText id={`${property}-${day.value}`} value={time ? time[property] : property === 'start' ? '09:00' : '17:00'} placeholder="HH:mm" maxLength={5}
                  disabled={saving || form.blockAllTime || !time} className={errors[`time${index}`] ? 'p-invalid' : ''} aria-invalid={Boolean(errors[`time${index}`])}
                  aria-label={`${day.label} ${property}`} aria-describedby={errors[`time${index}`] ? `day-${day.value}-error` : undefined}
                  onChange={event => change('times', form.times.map(item => item.dayOfWeek === day.value ? { ...item, [property]: event.target.value } : item))} />
              </Field>)}
              <Button label="Replicate time" title="Copy to all selected days" icon="pi pi-copy" className="p-button-text p-button-sm flag-definition-copy-time"
                disabled={saving || form.blockAllTime || !time || form.times.length < 2}
                onClick={() => change('times', form.times.map(item => ({ ...item, start: time.start, end: time.end })))} />
            </div>
            {time && errors[`time${index}`] && <small id={`day-${day.value}-error`} className="flag-error" role="alert">{errors[`time${index}`]}</small>}
          </div>;
        })}
      </div>
      {errors.times && <Message severity="error" text={errors.times} />}
      {[{ property: 'canOverride', title: 'Override Permission', hint: 'Which user levels are allowed to override this flag at the point of access.', label: 'Can override this flag' },
        { property: 'canAddRemove', title: 'Add / Remove Permission', hint: "Which user levels are allowed to add or remove this flag from a member's profile.", label: 'Can add / remove flag' }].map(section =>
        <section key={section.property} aria-labelledby={`${section.property}-heading`}>
          <h5 id={`${section.property}-heading`} className="flag-definition-section">{section.title}</h5>
          <p className="flag-definition-hint">{section.hint}</p>
          <div className="flag-definition-role-grid">
            {orderedPermissions(form).map(permission => <div key={permission.role} className="flag-definition-role-card">
              <div className="flag-definition-role-name">{FLAG_ROLE_LABELS[permission.role]}</div>
              <div className="flag-definition-permission-check">
                <Checkbox inputId={`${permission.role}-${section.property}`} checked={permission[section.property]} disabled={saving}
                  aria-label={`${FLAG_ROLE_LABELS[permission.role]} ${section.property === 'canOverride' ? 'override' : 'add/remove'}`}
                  onChange={event => change('permissions', form.permissions.map(item => item.role === permission.role ? { ...item, [section.property]: event.checked } : item))} />
                <label htmlFor={`${permission.role}-${section.property}`}>{section.label}</label>
              </div>
            </div>)}
          </div>
        </section>)}
      {errors.permissions && <Message severity="error" text={errors.permissions} />}
      <p className="flag-note mt-3">Override and add/remove permissions are independent. Duplicate names are accepted by the current backend.</p>
      </fieldset>
    </Dialog>
    <Dialog header={confirmation && confirmation.kind === 'reload' ? 'Discard edits and reload?' : 'Change flag status'}
      visible={Boolean(confirmation)} onHide={() => !saving && setConfirmation(null)} closable={!saving} modal
      footer={<><Button label="Cancel" className="p-button-text" disabled={saving} onClick={() => setConfirmation(null)} />
        <Button label="Confirm" disabled={saving} loading={saving} onClick={() => {
          if (confirmation.kind === 'reload') reloadEditor();
          else if (confirmation.kind === 'disable') disable(confirmation.flag);
          else { open({ ...confirmation.flag, active: true }); setConfirmation(null); }
        }} /></>}>
      {confirmation && confirmation.kind === 'reload' ? 'Loading the latest revision will discard your unsaved changes. Nothing will be overwritten on the server.' :
        'Disabling retains existing member assignments. Re-enabling may cause those assignments to apply again. Re-enable opens the editor; Save Flag confirms the server change.'}
    </Dialog>
  </FlagPage></div>;
};
export default FlagManagement;
